import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { pogConfetti } from "@/lib/pogConfetti";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MapSiegePill() {
  const [s, setS] = useState(null);
  const [victory, setVictory] = useState(false);
  const celebratedRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    const load = () => axios.get(`${API}/kingdom/siege`).then((r) => {
      setS(r.data);
      // First time this POG'R sees the castle defended today → celebrate 🦖🎉
      if (r.data.defeated && !celebratedRef.current) {
        let seen = false;
        try { seen = !!localStorage.getItem(`airpog_siege_win_${r.data.date}`); } catch { /* ignore */ }
        if (!seen) {
          celebratedRef.current = true;
          try { localStorage.setItem(`airpog_siege_win_${r.data.date}`, "1"); } catch { /* ignore */ }
          setVictory(true);
          pogConfetti();
          setTimeout(() => pogConfetti(), 500);
        }
      }
    }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  if (!s) return null;
  const pct = Math.min(100, Math.round((s.progress / s.goal) * 100));
  const won = s.defeated;
  const hoursLeft = 24 - new Date().getUTCHours();
  const urgent = !won && (pct < 30 || (hoursLeft <= 6 && pct < 100));

  return (
    <>
      <motion.button
        data-testid="map-siege-pill"
        onClick={() => navigate("/legend")}
        initial={{ opacity: 0, y: -8 }}
        animate={urgent
          ? { opacity: 1, y: 0, boxShadow: ["0 0 0 0 rgba(251,191,36,0.0)", "0 0 0 6px rgba(251,191,36,0.25)", "0 0 0 0 rgba(251,191,36,0.0)"] }
          : { opacity: 1, y: 0 }}
        transition={urgent ? { boxShadow: { duration: 1.3, repeat: Infinity, ease: "easeInOut" } } : {}}
        whileTap={{ scale: 0.96 }}
        title={won ? "Castle defended! Claim →" : urgent ? "Castle falling! Defend →" : "Defend the castle"}
        className={`absolute bottom-[16.25rem] left-2 z-[1200] w-10 h-10 rounded-full flex flex-col items-center justify-center gap-0.5 backdrop-blur-md shadow-lg text-white pointer-events-auto ${
          won
            ? "bg-gradient-to-b from-emerald-600 to-teal-600 ring-1 ring-emerald-200/60"
            : urgent
            ? "bg-gradient-to-b from-fuchsia-600 to-violet-700 ring-2 ring-amber-300/80"
            : "bg-gradient-to-b from-fuchsia-600 to-violet-600 ring-1 ring-fuchsia-200/50"
        }`}
      >
        <span className="text-base leading-none">{won ? "👑" : urgent ? "⚔️" : "🏰"}</span>
        <div className="w-5 h-1 rounded-full bg-black/25 overflow-hidden shrink-0">
          <div
            className={`h-full rounded-full ${urgent ? "bg-amber-300" : "bg-white/90"}`}
            style={{ width: `${Math.max(pct, 4)}%` }}
          />
        </div>
      </motion.button>

      {/* Victory celebration — first app-open after the castle is defended */}
      <AnimatePresence>
        {victory && (
          <motion.div
            key="siege-victory"
            data-testid="siege-victory-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setVictory(false)}
            className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm px-6"
          >
            <motion.div
              initial={{ scale: 0.7, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 18 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-sm w-full rounded-3xl bg-gradient-to-b from-slate-900 to-slate-950 ring-2 ring-amber-300/50 shadow-2xl shadow-amber-500/30 p-6 text-center"
            >
              <motion.img
                src="/pogzilla.png" alt="POGZILLA"
                initial={{ scale: 0.5, rotate: -8 }} animate={{ scale: [0.5, 1.15, 1], rotate: [-8, 4, 0] }}
                transition={{ duration: 0.7 }}
                className="w-28 h-28 mx-auto object-contain drop-shadow-[0_0_18px_rgba(52,211,153,0.7)]"
              />
              <p className="mt-2 text-[11px] font-heading font-black uppercase tracking-[0.22em] text-emerald-300">Victory!</p>
              <h2 className="mt-1 font-heading font-black text-2xl sm:text-3xl text-white leading-tight">
                POG KINGDOM is SAVED! 🏰
              </h2>
              <p className="mt-2 text-slate-300 text-sm">
                The POG MONSTERS have been beaten back — POGZILLA roars in triumph! 🦖 Every defender earned a bonus. Go claim yours!
              </p>
              <button
                data-testid="siege-victory-claim-btn"
                onClick={() => { setVictory(false); navigate("/legend"); }}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 text-amber-950 font-heading font-black px-6 py-3 hover:scale-105 active:scale-95 transition-transform"
              >
                Claim my defender bonus →
              </button>
              <button
                data-testid="siege-victory-close-btn"
                onClick={() => setVictory(false)}
                className="mt-2 w-full text-slate-400 text-xs font-bold py-1.5 hover:text-slate-200 transition-colors"
              >
                Maybe later
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
